// src/pages/PlanDetail.tsx
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";

const API_BASE =
  (import.meta as any).env?.VITE_API_BASE_URL ||
  (process as any).env?.REACT_APP_API_BASE_URL ||
  "";
const API_PREFIX = API_BASE ? `${API_BASE}/api/public` : "/api/public";

// 背景・ボタン・SOLD画像
const BG_IMG = "/assets/member/boatOKUman_LP_LP-29_haikei.webp";
const BUY_BTN = "/assets/member/red_button_buy_small.webp";
const SOLD_OVER = "/assets/member/soldout-01_result.webp";
const SOLD_BTN = "/assets/member/soldout-02_result.webp";

type PlanDetailT = {
  id: number | string;
  name: string;
  point?: number | string;
  price?: number;
  buyPt?: string;
  buyPoint?: number;

  date?: string;
  publishTime?: string;

  targetAmount?: number | string;
  stakeHint?: string;
  salesCount?: number;
  supplyCount?: number;

  imageUrl?: string;
  soldOut?: boolean;
  status?: string | number;
  saleStatus?: string | number;
  sellingStatus?: string | number;
  availability?: string | number;
  isSelling?: boolean;
  isAvailable?: boolean;
  active?: boolean;
  isPublished?: boolean;
  publish?: boolean; // ★ 管理画面の publish と合わせて見る
  [key: string]: any;
};

// 数値系のゆるいパーサ
const toNumber = (v: any): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v.replace(/[^\d]/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

// 数値＋単位の共通フォーマッタ
const formatNumberWithUnit = (
  value: any,
  unit: string,
  useComma: boolean = false
): string => {
  if (value === null || value === undefined || value === "") return "—";
  const n = toNumber(value);
  const base = useComma ? n.toLocaleString() : String(n);
  return `${base}${unit}`;
};

// 日時表示用フォーマッタ（今は未使用だが念のため残す）
const formatDateTime = (value?: string) => {
  if (!value) return "—";
  const s = String(value);
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;

  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");

  return `${y}-${m}-${day} ${hh}:${mm}`;
};

export default function PlanDetail() {
  const { id } = useParams();
  const [data, setData] = useState<PlanDetailT | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [bgSize, setBgSize] = useState<string>("100% auto");
  const [colWidth, setColWidth] = useState<number | string>("100%");
  const [heroOk, setHeroOk] = useState(true);

  // 背景レスポンシブ
  useEffect(() => {
    const handleResize = () => {
      const isWide = window.innerWidth >= 760;
      setBgSize(isWide ? "720px auto" : "100% auto");
      setColWidth(isWide ? 720 : "100%");
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ===== APIから詳細を取得（単体API版） =====
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const res = await fetch(`${API_PREFIX}/plan/${id}`, {
          cache: "no-store",
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error("プラン情報の取得に失敗しました。");
        }

        const p = (await res.json()) as PlanDetailT;

        if (!p || !p.id) {
          throw new Error("このプランは見つかりません。");
        }

        setData(p);
      } catch (e: any) {
        console.error(e);
        setErr(e?.message || "読み込みに失敗しました。");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading)
    return (
      <>
        <Header />
        <main className="lp">
          <div style={sx.wrap}>
            <div style={sx.card}>
              <div style={sx.body}>読み込み中…</div>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );

  if (err || !data)
    return (
      <>
        <Header />
        <main className="lp">
          <div style={sx.wrap}>
            <div style={{ ...sx.card, borderColor: "rgba(239,68,68,.35)" }}>
              <div style={sx.body}>
                {err || "このプランは見つかりませんでした。"}
              </div>
            </div>
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Link to="/plans" style={sx.btnGhost}>
                一覧に戻る
              </Link>
            </div>
          </div>
          <Footer />
        </main>
      </>
    );

  // ===== 売切れ判定 =====
  const isSoldOut = (() => {
    // 1) 管理画面 Plans.tsx の saleStatusOf と同じ考え方を優先
    // publish=false → 売切れ扱い
    if (data.publish === false || data.isPublished === false) {
      return true;
    }

    // 供給数（supplyCount）と販売数（salesCount）で埋まり切っているか
    const supply = toNumber(data.supplyCount);
    if (supply > 0) {
      const sold = toNumber(data.salesCount);
      if (sold >= supply) return true;
    }

    // 2) 後方互換：文字ステータス
    const s = String(
      data.status ??
        data.saleStatus ??
        data.sellingStatus ??
        data.availability ??
        ""
    )
      .toLowerCase()
      .trim();

    const soldWords = ["soldout", "売切れ", "停止", "非公開"];
    if (soldWords.includes(s)) return true;

    // 3) 後方互換：数値ステータス & フラグ群
    if (
      [0, 2].includes(Number(data.status)) ||
      data.isSelling === false ||
      data.isAvailable === false ||
      data.active === false ||
      data.soldOut === true
    ) {
      return true;
    }

    return false;
  })();

  const planImg =
    data.imageUrl ||
    `/assets/member/plans/${data.id}.webp` ||
    "/assets/no-image.webp";

  // ===== 販売ポイント =====
  const rawPt = (() => {
    const direct = toNumber(data.point);
    if (direct > 0) return direct;

    const price = toNumber(data.price);
    if (price > 0) return price;

    const buyPt = toNumber(data.buyPt);
    if (buyPt > 0) return buyPt;

    const buyPoint = toNumber(data.buyPoint);
    if (buyPoint > 0) return buyPoint;

    return 0;
  })();
  const salesPointText = formatNumberWithUnit(rawPt, " pt", false); // 0 でも「0 pt」

  // ===== 目標金額 =====
  const targetAmountLabel = formatNumberWithUnit(
    data.targetAmount,
    " 円",
    true
  ); // 20,000 円

  // ===== 提供数（≒販売枠） =====
  const supplyLabel = (() => {
    const src =
      data.supplyCount != null
        ? data.supplyCount
        : data.salesCount != null
        ? data.salesCount
        : null;
    return formatNumberWithUnit(src, " 点", false); // 5 点
  })();

  // ===== 推奨掛け金 =====
  const recommendedBetLabel = (() => {
    const raw = (data as any).recommendedBet ?? data.stakeHint;
    return formatNumberWithUnit(raw, " 円", true); // 500 円
  })();

  // ===== 説明テキスト（プラン名の下に表示） =====
  const desc =
    data.description ||
    data.detailsText ||
    data.details ||
    data.planDescription ||
    data.descriptionShort ||
    data.summary ||
    data.outline ||
    data.desc ||
    data.detail ||
    data.message ||
    "";

  return (
    <>
      <Header />
      <main className="lp">
        <div style={sx.page(BG_IMG, bgSize)}>
          <div aria-hidden style={sx.bgColumn(colWidth)} />

          {/* ==== プラン画像 ==== */}
          {planImg && heroOk && (
            <div style={sx.heroOuter(colWidth)}>
              <div style={sx.heroBox}>
                <img
                  src={planImg}
                  alt={data.name}
                  style={sx.heroImgFull}
                  onError={() => setHeroOk(false)}
                />

                {/* SOLD OUT オーバーレイ */}
                {isSoldOut && (
                  <img
                    src={SOLD_OVER}
                    alt="SOLD OUT"
                    style={sx.heroSoldOverlayFill}
                  />
                )}
              </div>
            </div>
          )}

          <div style={sx.wrap}>
            {/* ==== 提供情報 ==== */}
            <section style={sx.card}>
              <div style={sx.body}>
                <table style={sx.table}>
                  <tbody>
                    {/* プラン名 + 説明 */}
                    <tr>
                      <th style={sx.th}>プラン名</th>
                      <td style={sx.td}>
                        <div
                          style={{
                            fontWeight: 700,
                            marginBottom: desc ? 6 : 0,
                          }}
                        >
                          {data.name}
                        </div>
                        {desc && (
                          <div
                            style={{
                              fontSize: "0.9rem",
                              lineHeight: 1.6,
                              whiteSpace: "pre-line",
                            }}
                          >
                            {desc}
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* 販売ポイント */}
                    <Tr label="販売ポイント" value={salesPointText} />

                    {/* 提供数 */}
                    <Tr label="提供数" value={supplyLabel} />

                    {/* 開催日（時間部分カット） */}
                    <Tr
                      label="開催日"
                      value={(() => {
                        const raw =
                          (data.date as string | undefined) ||
                          (data.publishTime as string | undefined) ||
                          "";

                        if (!raw) return "—";

                        const s = String(raw);
                        if (s.includes("T")) return s.split("T")[0]; // 2025-12-06T09:00 → 2025-12-06
                        if (s.includes(" ")) return s.split(" ")[0]; // 2025-12-06 09:00 → 2025-12-06
                        return s; // もともと日付だけならそのまま
                      })()}
                    />

                    {/* 目標金額 */}
                    <Tr label="目標金額" value={targetAmountLabel} />

                    {/* 推奨掛け金 */}
                    <Tr label="推奨掛け金" value={recommendedBetLabel} />

                    {/* 購入特典（benefits） */}
                    <Tr label="購入特典" value={data.benefits || "—"} />
                  </tbody>
                </table>
              </div>
            </section>

            {/* ==== 注意文 ==== */}
            <div style={sx.alertBox}>
              <div style={sx.alertHead}>ご注意ください</div>
              <p style={sx.alertText}>
                弊社が会員様に提供させていただく商品は「確保（買い目）」であり、
                いかなる場合でも決済完了後の返金・返品はお応じられません。
              </p>
            </div>

            {/* ==== ボタン ==== */}
            <div style={sx.buyBox}>
              {isSoldOut ? (
                <img src={SOLD_BTN} alt="完売御礼" style={sx.buyImg} />
              ) : (
                <Link to={`/plans/${data.id}/purchase`} style={sx.buyLink}>
                  <img src={BUY_BTN} alt="購入画面へ" style={sx.buyImg} />
                </Link>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
}

function Tr({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <th style={sx.th}>{label}</th>
      <td style={sx.td}>{value}</td>
    </tr>
  );
}

const sx: any = {
  page: (bg: string, size: string): React.CSSProperties => ({
    position: "relative",
    minHeight: "100vh",
    backgroundImage: `url('${bg}')`,
    backgroundPosition: "center top",
    backgroundRepeat: "repeat-y",
    backgroundSize: size,
  }),
  bgColumn: (w: number | string): React.CSSProperties => ({
    position: "absolute",
    inset: 0,
    margin: "0 auto",
    width: typeof w === "number" ? `${w}px` : w,
    background: "#fff",
    boxShadow: "0 0 0 1px #e6edf5, 0 6px 20px rgba(0,0,0,.06)",
    pointerEvents: "none",
  }),
  heroOuter: (w: number | string): React.CSSProperties => ({
    width: typeof w === "number" ? `${w}px` : w,
    margin: "0 auto",
    padding: "12px 10px 0",
    position: "relative",
    zIndex: 1,
  }),
  heroBox: {
    position: "relative",
    borderRadius: 12,
    overflow: "hidden",
    background: "#fff",
    border: "1px solid #e6edf5",
    boxShadow: "0 4px 12px rgba(0,0,0,.06)",
  },
  heroImgFull: {
    display: "block",
    width: "100%",
    height: "auto",
    userSelect: "none",
  },
  heroSoldOverlayFill: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain" as const,
    pointerEvents: "none",
  },
  wrap: {
    maxWidth: 720,
    margin: "0 auto",
    padding: "12px 10px 18px",
    position: "relative",
    zIndex: 1,
  },
  card: {
    background: "#fff",
    border: "1px solid #e6edf5",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,.06)",
    overflow: "hidden",
  },
  body: { padding: 12 },
  table: { width: "100%", borderCollapse: "collapse" as const },
  th: {
    width: 120,
    textAlign: "left" as const,
    background: "#f5f8fb",
    borderBottom: "1px solid #e9eff6",
    padding: "10px 12px",
    fontWeight: 800,
    verticalAlign: "top" as const,
  },
  td: { borderBottom: "1px solid #e9eff6", padding: "10px 12px" },
  alertBox: {
    marginTop: 16,
    background: "#fff",
    border: "1px solid #e6edf5",
    borderRadius: 6,
    overflow: "hidden",
  },
  alertHead: {
    background: "#1e2a38",
    color: "#fff",
    fontWeight: 800,
    textAlign: "center" as const,
    padding: "8px 0",
    fontSize: "1rem",
  },
  alertText: {
    margin: 0,
    padding: "12px 16px",
    fontSize: "0.92rem",
    color: "#111",
    lineHeight: 1.7,
  },
  buyBox: { textAlign: "center" as const, marginTop: 18 },
  buyLink: { display: "inline-block", lineHeight: 0 },
  buyImg: {
    display: "block",
    width: 260,
    height: "auto",
    margin: "0 auto",
    userSelect: "none",
  },
  btnGhost: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 12,
    textDecoration: "none",
    color: "#111827",
    background: "transparent",
    fontWeight: 800,
    border: "1px solid rgba(0,0,0,.15)",
  },
};
